// File: structural_app/frontend/src/components/BeamFormulae/BeamVisualization.js
// Purpose: This component renders an SVG visualization of the beam based on the provided spans, 
// loads, and boundary conditions. It includes helper functions for drawing various elements.

import React, { useEffect, useRef, useCallback, useContext, forwardRef } from 'react';
// import { Paper } from '@mui/material'; // Unused import
import './BeamVisualization.css';
// Keep SupportIcons import ONLY if needed elsewhere, otherwise remove
// import { FixedIcon, PinnedIcon, SpringIcon } from './SupportIcons';
import BeamContext from '../../context/BeamContext'; // Import the context

// Component responsible for rendering the SVG visualization of the beam, including spans, supports, and loads, based on the provided props.

// --- Define Helper Functions OUTSIDE the component --- 
// (Or define them INSIDE renderBeam if they need access to its scope directly)

// Ensure renderSupport accepts beamHeight and availableWidth to position/size correctly
const renderSupport = (g, x, beamCenterY, type, supportHeight, beamHeight, availableWidth) => {
    console.log(`Rendering support: type='${type}', x=${x.toFixed(1)}`);
    const supportGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    const beamBottomY = beamCenterY + beamHeight / 2;

    supportGroup.innerHTML = ''; // Clear previous content
    let hasContent = false;

    // --- Direct SVG Drawing Logic (mimicking SupportIcons.js) --- 
    switch (type) {
        case 'fixed':
            const fixedVLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            fixedVLine.setAttribute('x1', '4'); fixedVLine.setAttribute('y1', '2');
            fixedVLine.setAttribute('x2', '4'); fixedVLine.setAttribute('y2', '22');
            fixedVLine.setAttribute('stroke', 'currentColor'); fixedVLine.setAttribute('stroke-width', '1.5');
            supportGroup.appendChild(fixedVLine);
            const fixedHatch1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            fixedHatch1.setAttribute('x1', '4'); fixedHatch1.setAttribute('y1', '5');
            fixedHatch1.setAttribute('x2', '10'); fixedHatch1.setAttribute('y2', '11');
            fixedHatch1.setAttribute('stroke', 'currentColor'); fixedHatch1.setAttribute('stroke-width', '1');
            supportGroup.appendChild(fixedHatch1);
            const fixedHatch2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            fixedHatch2.setAttribute('x1', '4'); fixedHatch2.setAttribute('y1', '10');
            fixedHatch2.setAttribute('x2', '10'); fixedHatch2.setAttribute('y2', '16');
            fixedHatch2.setAttribute('stroke', 'currentColor'); fixedHatch2.setAttribute('stroke-width', '1');
            supportGroup.appendChild(fixedHatch2);
            const fixedHatch3 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            fixedHatch3.setAttribute('x1', '4'); fixedHatch3.setAttribute('y1', '15');
            fixedHatch3.setAttribute('x2', '10'); fixedHatch3.setAttribute('y2', '21');
            fixedHatch3.setAttribute('stroke', 'currentColor'); fixedHatch3.setAttribute('stroke-width', '1');
            supportGroup.appendChild(fixedHatch3);
            hasContent = true;
            break;
        case 'pinned':
            const pinnedPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            pinnedPath.setAttribute('d', 'M12 2L2 22h20L12 2z');
            pinnedPath.setAttribute('fill', 'none'); pinnedPath.setAttribute('stroke', 'currentColor');
            pinnedPath.setAttribute('stroke-width', '1.5');
            supportGroup.appendChild(pinnedPath);
            const pinnedCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            pinnedCircle.setAttribute('cx', '12'); pinnedCircle.setAttribute('cy', '21.5');
            pinnedCircle.setAttribute('r', '1'); pinnedCircle.setAttribute('fill', 'currentColor');
            supportGroup.appendChild(pinnedCircle);
            hasContent = true;
            break;
        case 'spring':
            const turns = 3;
            const radiusGrowth = 9;
            const startRadius = 1;
            const numSegments = turns * 16;
            let pathData = "M 12,12";
            for (let i = 0; i <= numSegments; i++) {
                const angle = (i / numSegments) * turns * 2 * Math.PI;
                const radius = startRadius + radiusGrowth * (i / numSegments);
                const xPos = 12 + radius * Math.cos(angle);
                const yPos = 12 + radius * Math.sin(angle);
                pathData += ` L ${xPos.toFixed(2)},${yPos.toFixed(2)}`;
            }
            const springPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            springPath.setAttribute('d', pathData);
            springPath.setAttribute('fill', 'none'); springPath.setAttribute('stroke', 'currentColor');
            springPath.setAttribute('stroke-width', '1.5');
            supportGroup.appendChild(springPath);
            hasContent = true;
            break;
        case 'free':
        default:
            // Ensure nothing is drawn
            hasContent = false;
            break;
    }
    // --- End Direct SVG Drawing --- 

    if (hasContent) {
        const scaleFactor = 0.8;
        const iconWidth = 24;
        const iconHeight = 24;
        const scaledWidth = iconWidth * scaleFactor;
        const scaledHeight = iconHeight * scaleFactor;
        
        // Calculate vertical position based on type
        let translateY;
        if (type === 'fixed' || type === 'spring') {
            // Center fixed and spring icons vertically with the beam
            translateY = beamCenterY - (scaledHeight / 2);
        } else { 
            // Place pinned icon base slightly below the beam bottom (as before)
            translateY = beamBottomY + 2; 
        }

        // Center the icon horizontally
        const translateX = x - scaledWidth / 2;
        
        const transform = `translate(${translateX}, ${translateY}) scale(${scaleFactor})`; 
        supportGroup.setAttribute('transform', transform);
        // Set stroke/fill on the group for children to inherit
        supportGroup.setAttribute('stroke', '#333');
        supportGroup.setAttribute('fill', '#666');
        g.appendChild(supportGroup);
        console.log('Appended supportGroup with direct SVG elements to main G');
    } else {
         console.log(`Support type '${type}', no icon element rendered as expected.`);
    }
};

// Helper function to render a point load
const renderPointLoad = (g, x, beamTopY, beamBottomY, magnitude, availableWidth) => {
    console.log(`  renderPointLoad called: x=${x.toFixed(1)}, magnitude=${magnitude}, beamTopY=${beamTopY.toFixed(1)}, beamBottomY=${beamBottomY.toFixed(1)}, availableWidth=${availableWidth.toFixed(1)}`);

    const dynamicArrowLength = Math.min(30, Math.max(15, availableWidth * 0.06));
    const dynamicArrowHeadWidth = Math.min(8, Math.max(4, availableWidth * 0.015));
    const dynamicOffset = availableWidth * 0.1;
    console.log(`    Arrow dynamicLength: ${dynamicArrowLength.toFixed(1)}, headWidth: ${dynamicArrowHeadWidth.toFixed(1)}, offset: ${dynamicOffset.toFixed(1)}`);

    const direction = magnitude >= 0 ? 1 : -1;
    let startY, endY;
    if (direction === 1) { startY = beamTopY - dynamicOffset; endY = beamTopY - 2; }
    else { startY = beamBottomY + dynamicOffset; endY = beamBottomY + 2; }
    console.log(`    Direction: ${direction}, StartY: ${startY.toFixed(1)}, EndY: ${endY.toFixed(1)}`);

    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', x); line.setAttribute('y1', startY);
    line.setAttribute('x2', x); line.setAttribute('y2', endY);
    line.setAttribute('stroke', 'blue'); line.setAttribute('stroke-width', '2');
    console.log(`    Appending line from (${x.toFixed(1)}, ${startY.toFixed(1)}) to (${x.toFixed(1)}, ${endY.toFixed(1)})`);
    g.appendChild(line);

    const arrowHead = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const arrowHeadPathData = `M ${x} ${endY} L ${x - dynamicArrowHeadWidth} ${endY - direction * dynamicArrowHeadWidth * 1.5} L ${x + dynamicArrowHeadWidth} ${endY - direction * dynamicArrowHeadWidth * 1.5} Z`;
    console.log(`    Arrowhead path d="${arrowHeadPathData}"`);
    arrowHead.setAttribute('d', arrowHeadPathData);
    arrowHead.setAttribute('fill', 'blue');
    console.log(`    Appending arrowhead`);
    g.appendChild(arrowHead);

    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    const labelX = x + 5;
    const labelY = startY + (direction === 1 ? -5 : 15);
    label.setAttribute('x', labelX); label.setAttribute('y', labelY);
    label.setAttribute('text-anchor', 'start'); label.setAttribute('fill', 'blue');
    label.setAttribute('font-size', '12px'); label.textContent = `${magnitude} kN`;
    console.log(`    Appending label "${magnitude} kN" at (${labelX.toFixed(1)}, ${labelY.toFixed(1)})`);
    g.appendChild(label);

    console.log(`  renderPointLoad finished for magnitude ${magnitude}`);
};

// Calculates combined line load segments across the entire beam
const calculateLineLoadSegments = (spans, totalLength) => {
    const lineLoads = [];
    let spanStartOffset = 0;

    const parseEdge = (raw, emptyDefault) => {
        if (raw === '' || raw == null) return emptyDefault;
        const n = parseFloat(String(raw).replace(',', '.'));
        return Number.isFinite(n) ? n : emptyDefault;
    };

    spans.forEach(span => {
        const spanLength = parseFloat(span.length) || 0;
        const maxAlongSpan = spanLength;
        span.loads?.forEach(load => {
            if (load.type !== 'line') return;
            const loadMagnitude = parseFloat(load.magnitude || 0);
            if (Math.abs(loadMagnitude) < 1e-9 || isNaN(loadMagnitude)) return;

            const isGlobal = load.coordinateSystem === 'global';
            const maxAlong = isGlobal ? totalLength : maxAlongSpan;

            const effStart = parseEdge(load.startPosition, 0);
            const effEnd = parseEdge(load.endPosition, maxAlong);

            let absoluteStart;
            let absoluteEnd;
            if (isGlobal) {
                absoluteStart = effStart;
                absoluteEnd = effEnd;
            } else {
                absoluteStart = spanStartOffset + effStart;
                absoluteEnd = spanStartOffset + effEnd;
            }

            const tol = 1e-6;
            if (
                absoluteStart + tol < absoluteEnd
                && absoluteEnd <= totalLength + tol
                && absoluteStart >= -tol
            ) {
                lineLoads.push({
                    start: absoluteStart,
                    end: absoluteEnd,
                    magnitude: loadMagnitude,
                });
            }
        });
        spanStartOffset += spanLength;
    });

    if (lineLoads.length === 0) return [];

    // 2. Get all unique start/end points
    const points = new Set([0]);
    lineLoads.forEach(load => { points.add(load.start); points.add(load.end); });
    points.add(totalLength);
    const sortedPoints = Array.from(points).filter(p => p >= 0 && p <= totalLength).sort((a, b) => a - b);

    // 3. Create segments and calculate combined magnitude
    const segments = [];
    for (let i = 0; i < sortedPoints.length - 1; i++) {
        const start = sortedPoints[i];
        const end = sortedPoints[i + 1];
        if (end - start < 1e-9) continue; // Skip zero-length segments
        
        const midPoint = (start + end) / 2;
        let combinedMagnitude = 0;
        lineLoads.forEach(load => {
            if (load.start <= midPoint && load.end >= midPoint) {
                combinedMagnitude += load.magnitude;
            }
        });

        if (Math.abs(combinedMagnitude) > 1e-9) {
             segments.push({ start, end, magnitude: combinedMagnitude });
        }
    }
    console.log("Calculated Segments:", segments);
    return segments;
};

// --- Renamed renderLineLoad to render a SEGMENT --- 
const renderLineLoadSegment = (g, startX_svg, endX_svg, magnitude, beamTopY, beamBottomY, availableWidth) => {
    const lineLength_svg = endX_svg - startX_svg;
    if (lineLength_svg <= 1e-6) return;
    console.log(`  renderLineLoadSegment called: magnitude=${magnitude.toFixed(2)}, startX_svg=${startX_svg.toFixed(1)}, endX_svg=${endX_svg.toFixed(1)}`);
    const direction = magnitude >= 0 ? 1 : -1;
    const dynamicOffset = availableWidth * 0.05;
    const arrowHeadWidth = Math.min(6, Math.max(3, availableWidth * 0.01));
    const color = "lightseagreen";
    let startY, beamEdgeY;
    if (direction === 1) { startY = beamTopY - dynamicOffset; beamEdgeY = beamTopY - 2; }
    else { startY = beamBottomY + dynamicOffset; beamEdgeY = beamBottomY + 2; }
    const connectLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    connectLine.setAttribute('x1', startX_svg); connectLine.setAttribute('y1', startY);
    connectLine.setAttribute('x2', endX_svg); connectLine.setAttribute('y2', startY);
    connectLine.setAttribute('stroke', color); connectLine.setAttribute('stroke-width', '1');
    connectLine.setAttribute('stroke-dasharray', '4,4');
    g.appendChild(connectLine);
    const startLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    startLine.setAttribute('x1', startX_svg); startLine.setAttribute('y1', startY);
    startLine.setAttribute('x2', startX_svg); startLine.setAttribute('y2', beamEdgeY);
    startLine.setAttribute('stroke', color); startLine.setAttribute('stroke-width', '1');
    g.appendChild(startLine);
    const startArrowHead = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    startArrowHead.setAttribute('d', `M ${startX_svg} ${beamEdgeY} L ${startX_svg - arrowHeadWidth} ${beamEdgeY - direction * arrowHeadWidth * 1.5} L ${startX_svg + arrowHeadWidth} ${beamEdgeY - direction * arrowHeadWidth * 1.5} Z`);
    startArrowHead.setAttribute('fill', color);
    g.appendChild(startArrowHead);
    const endLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    endLine.setAttribute('x1', endX_svg); endLine.setAttribute('y1', startY);
    endLine.setAttribute('x2', endX_svg); endLine.setAttribute('y2', beamEdgeY);
    endLine.setAttribute('stroke', color); endLine.setAttribute('stroke-width', '1');
    g.appendChild(endLine);
    const endArrowHead = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    endArrowHead.setAttribute('d', `M ${endX_svg} ${beamEdgeY} L ${endX_svg - arrowHeadWidth} ${beamEdgeY - direction * arrowHeadWidth * 1.5} L ${endX_svg + arrowHeadWidth} ${beamEdgeY - direction * arrowHeadWidth * 1.5} Z`);
    endArrowHead.setAttribute('fill', color);
    g.appendChild(endArrowHead);
    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    label.setAttribute('x', startX_svg + lineLength_svg / 2);
    label.setAttribute('y', startY - direction * 5);
    label.setAttribute('text-anchor', 'middle');
    label.setAttribute('fill', color);
    label.setAttribute('font-size', '10px');
    label.textContent = `${magnitude.toLocaleString(undefined, {minimumFractionDigits: 1, maximumFractionDigits: 2})} kN/m`;
    g.appendChild(label);
};

const renderMomentLoad = (g, x, beamCenterY, magnitude, availableWidth) => {
    const radius = availableWidth * 0.04;
    const arrowSize = 6;
    const direction = magnitude >= 0 ? 1 : -1;
    const arc = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const startAngle = direction === 1 ? -Math.PI / 2 : Math.PI / 2;
    const endAngle = direction === 1 ? Math.PI * 1.2 : -Math.PI * 1.2;
    const startX = x + radius * Math.cos(startAngle); const startY = beamCenterY + radius * Math.sin(startAngle);
    const endX = x + radius * Math.cos(endAngle); const endY = beamCenterY + radius * Math.sin(endAngle);
    const largeArcFlag = Math.abs(endAngle - startAngle) > Math.PI ? 1 : 0;
    const sweepFlag = direction === 1 ? 1 : 0;
    arc.setAttribute('d', `M ${startX},${startY} A ${radius},${radius} 0 ${largeArcFlag} ${sweepFlag} ${endX},${endY}`);
    arc.setAttribute('fill', 'none'); arc.setAttribute('stroke', 'purple'); arc.setAttribute('stroke-width', '2');
    g.appendChild(arc);
    const arrowHead = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const angle = endAngle + (direction * Math.PI / 2);
    const headBaseX = endX + Math.cos(angle) * arrowSize * 0.5; const headBaseY = endY + Math.sin(angle) * arrowSize * 0.5;
    const headTipX = endX - Math.cos(angle) * arrowSize * 0.5; const headTipY = endY - Math.sin(angle) * arrowSize * 0.5;
    const wingAngle = Math.PI / 6;
    const wing1X = headBaseX + Math.cos(angle + wingAngle + Math.PI) * arrowSize; const wing1Y = headBaseY + Math.sin(angle + wingAngle + Math.PI) * arrowSize;
    const wing2X = headBaseX + Math.cos(angle - wingAngle + Math.PI) * arrowSize; const wing2Y = headBaseY + Math.sin(angle - wingAngle + Math.PI) * arrowSize;
    arrowHead.setAttribute('d', `M ${headTipX},${headTipY} L ${wing1X},${wing1Y} L ${wing2X},${wing2Y} Z`);
    arrowHead.setAttribute('fill', 'purple');
    g.appendChild(arrowHead);
    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    label.setAttribute('x', x); label.setAttribute('y', beamCenterY - radius - 10);
    label.setAttribute('text-anchor', 'middle'); label.setAttribute('fill', 'purple');
    label.setAttribute('font-size', '12px'); label.textContent = `${magnitude} kNm`;
    g.appendChild(label);
};

const BeamVisualization = forwardRef(function BeamVisualization(_props, forwardedRef) {
  const svgRef = useRef(null);
  const setSvgRef = useCallback(
    (el) => {
      svgRef.current = el;
      if (typeof forwardedRef === 'function') {
        forwardedRef(el);
      } else if (forwardedRef != null) {
        forwardedRef.current = el;
      }
    },
    [forwardedRef],
  );
  // Ref for the debounce timer
  const debounceTimerRef = useRef(null);
  // Get data from context
  const { spans, selectedSection } = useContext(BeamContext);

  // --- renderBeam Function ---
  // Use useCallback to memoize the render function
  const renderBeam = useCallback(() => {
    const svg = svgRef.current;
    if (!svg || !spans || spans.length === 0) {
        console.warn("renderBeam: SVG ref not ready or no spans data.");
        // Clear previous drawing if no spans
        if (svg) svg.innerHTML = '';
        return;
    }

    // Clear previous drawing
    svg.innerHTML = '';
    console.log("renderBeam: Starting rendering...");
    console.log("Spans data:", JSON.parse(JSON.stringify(spans)));
    console.log("Selected Section data:", selectedSection);

    // Configuration
    const padding = 50; // Increased padding for labels/loads
    const beamHeight = 15; // Visual height of the beam
    const supportHeight = 20; // Visual height for supports
    const availableWidth = svg.clientWidth - 2 * padding;
    const svgHeight = svg.clientHeight;
    const beamCenterY = svgHeight / 2;
    const beamTopY = beamCenterY - beamHeight / 2;
    const beamBottomY = beamCenterY + beamHeight / 2;

    const totalLength = spans.reduce((sum, span) => sum + (parseFloat(span.length) || 0), 0);
    if (totalLength <= 0) {
        console.warn("renderBeam: Total length is zero or negative, cannot render.");
        // Optionally render a message or empty state
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', padding);
        text.setAttribute('y', beamCenterY);
        text.setAttribute('fill', 'grey');
        text.textContent = 'Define beam spans to visualize.';
        svg.appendChild(text);
        return;
    }
    const scaleX = availableWidth / totalLength;

    console.log(`SVG Dimensions: width=${svg.clientWidth}, height=${svg.clientHeight}`);
    console.log(`Calculated: availableWidth=${availableWidth.toFixed(1)}, totalLength=${totalLength.toFixed(1)}, scaleX=${scaleX.toFixed(2)}`);

    // Create a group for all beam elements, apply transform
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.setAttribute('transform', `translate(${padding}, 0)`);
    svg.appendChild(g);

    // Draw beam spans
    let currentX = 0;
    spans.forEach((span, index) => {
        const spanLength = parseFloat(span.length) || 0;
        if (spanLength <= 0) return; // Skip zero-length spans

        const startX_svg = currentX * scaleX;
        const endX_svg = (currentX + spanLength) * scaleX;
        console.log(`Span ${index}: length=${spanLength.toFixed(1)}, startX_svg=${startX_svg.toFixed(1)}, endX_svg=${endX_svg.toFixed(1)}`);

        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', startX_svg);
        rect.setAttribute('y', beamTopY);
        rect.setAttribute('width', (endX_svg - startX_svg));
        rect.setAttribute('height', beamHeight);
        rect.setAttribute('fill', '#ccc');
        rect.setAttribute('stroke', '#666');
        rect.setAttribute('stroke-width', '1');
        g.appendChild(rect);

        // Draw Span Length Label (centered below span)
        const labelX = startX_svg + (endX_svg - startX_svg) / 2;
        const labelY = beamBottomY + 30; // Position below supports
        const lengthLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        lengthLabel.setAttribute('x', labelX);
        lengthLabel.setAttribute('y', labelY);
        lengthLabel.setAttribute('text-anchor', 'middle');
        lengthLabel.setAttribute('font-size', '12px');
        lengthLabel.setAttribute('fill', '#333');
        lengthLabel.textContent = `${spanLength.toFixed(2)}m`;
        g.appendChild(lengthLabel);

        // Draw boundary conditions (supports)
        // Left boundary for the first span
        if (index === 0) {
            console.log(`Attempting renderSupport for first span left: type='${span.leftBoundary}', x=${startX_svg.toFixed(1)}`);
            renderSupport(g, startX_svg, beamCenterY, span.leftBoundary, supportHeight, beamHeight, availableWidth);
        }
        // Right boundary for every span (this will be the shared node)
        console.log(`Attempting renderSupport for span ${index} right: type='${span.rightBoundary}', x=${endX_svg.toFixed(1)}`);
        renderSupport(g, endX_svg, beamCenterY, span.rightBoundary, supportHeight, beamHeight, availableWidth);

        // Draw loads within this span
        const spanStartX_abs = currentX; // Absolute position of span start
        span.loads?.forEach(load => {
            const magnitude = parseFloat(load.magnitude || 0);
            if (isNaN(magnitude) || Math.abs(magnitude) < 1e-9) return; // Skip invalid/zero loads

            if (load.type === 'point') {
                 const position = parseFloat(load.position || 0);
                 if (!isNaN(position) && position >= 0 && position <= spanLength) {
                     const loadX_abs = spanStartX_abs + position;
                     const loadX_svg = loadX_abs * scaleX;
                     console.log(`  Load (Point): mag=${magnitude}, pos=${position.toFixed(1)}, loadX_svg=${loadX_svg.toFixed(1)}`);
                     renderPointLoad(g, loadX_svg, beamTopY, beamBottomY, magnitude, availableWidth);
                 } else {
                      console.warn("Skipping point load with invalid position:", load);
                 }
            } else if (load.type === 'moment') {
                 const position = parseFloat(load.position || 0);
                 if (!isNaN(position) && position >= 0 && position <= spanLength) {
                     const loadX_abs = spanStartX_abs + position;
                     const loadX_svg = loadX_abs * scaleX;
                     console.log(`  Load (Moment): mag=${magnitude}, pos=${position.toFixed(1)}, loadX_svg=${loadX_svg.toFixed(1)}`);
                     renderMomentLoad(g, loadX_svg, beamCenterY, magnitude, availableWidth);
                 } else {
                     console.warn("Skipping moment load with invalid position:", load);
                 }
            }
            // Line loads handled separately below
        });

        currentX += spanLength;
    });

     // Draw line loads (calculated across the entire beam)
     const lineLoadSegments = calculateLineLoadSegments(spans, totalLength);
     console.log("Calculated Line Load Segments:", JSON.parse(JSON.stringify(lineLoadSegments)));
     lineLoadSegments.forEach(segment => {
         const startX_svg = segment.start * scaleX;
         const endX_svg = segment.end * scaleX;
         console.log(`Drawing Line Load Segment: start=${segment.start.toFixed(1)}, end=${segment.end.toFixed(1)}, mag=${segment.magnitude}, startX_svg=${startX_svg.toFixed(1)}, endX_svg=${endX_svg.toFixed(1)}`);
         renderLineLoadSegment(g, startX_svg, endX_svg, segment.magnitude, beamTopY, beamBottomY, availableWidth);
     });

    console.log("renderBeam: Rendering finished.");

  }, [spans, selectedSection]); // Depend on spans and selectedSection from context

  // Effect to run rendering function when dependencies change
  useEffect(() => {
    // Add a resize observer to re-render on resize
    const resizeObserver = new ResizeObserver(entries => {
        // Debounce using the ref
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            console.log("Resize detected, re-rendering beam visualization.");
            renderBeam();
        }, 100); // Adjust debounce time as needed
    });

    const svgElement = svgRef.current; // Capture ref value for cleanup
    if (svgElement) {
        resizeObserver.observe(svgElement);
        renderBeam(); // Initial render
    }

    // Cleanup observer on component unmount
    return () => {
        if (svgElement) {
            resizeObserver.unobserve(svgElement);
        }
        // Clear the timer using the ref on unmount
        if (debounceTimerRef.current) {
             clearTimeout(debounceTimerRef.current);
        }
    };
  }, [renderBeam]); // Depend on the memoized renderBeam function

  return (
    <div className="beam-visualization-container" style={{ width: '100%', height: '100%', minHeight: '200px' }}>
      <svg ref={setSvgRef} width="100%" height="100%"></svg>
    </div>
  );
});

export default BeamVisualization;
